/**
 * Avatar Utility
 * 
 * Utility functions for handling user avatars and profile pictures
 * Focuses on caching and optimizing URLs to avoid rate limiting
 */

import { User } from '../types/auth';

/**
 * Fix Google profile picture URL to avoid 429 errors
 * Google profile pictures can return 429 errors when accessed too frequently
 * This function modifies the URL to use a different size parameter and removes tracking
 */
const fixGoogleProfilePictureUrl = (url: string): string => {
  // Parse the URL to remove any tracking parameters
  const baseUrl = url.split('=')[0];
  
  // Add a size parameter that works reliably
  // Using s128-c instead of s96-c (default) for better caching
  return `${baseUrl}=s128-c`;
};

// Cache for avatar URLs to avoid repeated requests
const avatarCache = new Map<string, string>();

/**
 * Get a reliable avatar URL for a user
 * This handles Google profile pictures that might return 429 errors
 * by fixing the URL parameters and implements browser-side caching
 * 
 * Note: This is primarily used for backward compatibility.
 * New components should use the UserAvatar component directly.
 */
export const getAvatarUrl = (user: User | null | undefined): string => {
  if (!user || !user.picture) {
    return '';
  }

  // Check cache first
  const cacheKey = user.id + user.picture;
  if (avatarCache.has(cacheKey)) {
    return avatarCache.get(cacheKey) as string;
  }

  // Fix Google profile picture URLs
  let optimizedUrl = user.picture;
  if (user.picture.includes('googleusercontent.com')) {
    optimizedUrl = fixGoogleProfilePictureUrl(user.picture);
  }

  // Cache the result
  avatarCache.set(cacheKey, optimizedUrl);
  return optimizedUrl;
};

/**
 * Clear the avatar URL cache
 * Useful when logging out or when profile pictures might have changed
 */
export const clearAvatarCache = (): void => {
  avatarCache.clear();
}; 